import {
  type HubCamera,
  type HubCameraConfigInfo,
  type HubCameraExampleStreamConfig,
  HubCameraStatus,
  HubCameraType,
} from '@robovision/hub'
import { i18n } from '~i18n'


export interface CameraParams {
  cameraType: HubCameraType
  createdAt: Date
  id: string
  name: string
  serialNumber: string
  status: HubCameraStatus
  thumbnail?: File
  usedInConfigs?: HubCameraConfigInfo[]
}

export class Camera {
  static readonly modelName = 'Camera'

  public usedInConfigs: HubCameraConfigInfo[]
  public cameraType: HubCameraType
  public createdAt: Date
  public id: string
  public name: string
  public serialNumber: string
  public status: HubCameraStatus
  public thumbnail?: File

  constructor (params: CameraParams) {
    this.usedInConfigs = params.usedInConfigs ?? []
    this.cameraType = params.cameraType
    this.createdAt = params.createdAt
    this.id = params.id
    this.name = params.name
    this.serialNumber = params.serialNumber
    this.status = params.status
    this.thumbnail = params.thumbnail
  }

  public get statusLabel (): string {
    switch (this.status) {
      case HubCameraStatus.IDLE:
        return i18n.t('cameras.titles.idle')
      case HubCameraStatus.IN_USE:
        return i18n.t('cameras.titles.inUse')
      default:
        return i18n.t('cameras.titles.invalid')
    }
  }

  public get cameraTypeLabel (): string {
    switch (this.cameraType) {
      case HubCameraType.ALLIED_VISION:
        return i18n.t('cameras.types.alliedVision')
      case HubCameraType.BASLER:
        return i18n.t('cameras.types.basler')
      default:
        return ''
    }
  }

  public get isActive (): boolean {
    return this.status === HubCameraStatus.IN_USE
  }

  public get isReady (): boolean {
    return this.status === HubCameraStatus.IDLE
  }

  public static fromHubCameraResponse (data: HubCamera): Camera {
    return new Camera({
      cameraType: data.cameraType,
      createdAt: new Date(data.createdAt),
      id: data.id,
      name: data.name,
      serialNumber: data.serialNumber,
      status: data.status,
      usedInConfigs: data.usedInConfigs,
    })
  }

  public static fromHubCameraExampleStreamConfig (data: HubCameraExampleStreamConfig): Camera {
    return new Camera({
      cameraType: data.cameraType,
      createdAt: new Date(data.cameraCreatedAt),
      id: data.cameraId,
      name: data.cameraName,
      serialNumber: data.cameraSerialNumber,
      status: data.cameraStatus,
    })
  }
}
